import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, Spinner, EmptyState } from "@/components/ui-bits";
import { Plus, Clock, MapPin, Users as UsersIcon, Calendar, Sparkles } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const MONTHS_ES = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
const MONTHS_EN = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];

const splitDate = (iso, lang) => {
  if (!iso) return { day: "—", month: "" };
  const d = new Date(iso);
  const months = lang === "en" ? MONTHS_EN : MONTHS_ES;
  return { day: String(d.getDate()).padStart(2, "0"), month: months[d.getMonth()].toUpperCase(), year: d.getFullYear() };
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};

export default function Trabajos() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initialFilter = ["programado", "en_curso", "finalizado"].includes(searchParams.get("filter"))
    ? searchParams.get("filter") : "en_curso";
  const [items, setItems] = useState([]);
  const [embs, setEmbs] = useState([]);
  const [users, setUsers] = useState([]);
  const [servs, setServs] = useState([]);
  const [filter, setFilter] = useState(initialFilter);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const counts = useMemo(() => ({
    en_curso: items.filter((x) => x.status === "en_curso" || x.status === "pausado").length,
    // "Con retraso" sigue pendiente: se cuenta y se lista con los programados (con su etiqueta roja).
    programado: items.filter((x) => x.status === "programado" || x.status === "retraso").length,
    finalizado: items.filter((x) => x.status === "finalizado").length,
  }), [items]);

  const FILTERS = [
    { key: "en_curso", label: t("trabajos.filter.in_progress") },
    { key: "programado", label: t("trabajos.filter.scheduled") },
    { key: "finalizado", label: t("trabajos.filter.completed") },
  ];

  const reload = async () => {
    setLoading(true);
    setErr("");
    try {
      const [m, e, s, u] = await Promise.all([
        api.get("/mantenimientos"),
        api.get("/embarcaciones"),
        api.get("/servicios"),
        api.get("/users").catch(() => null),
      ]);
      setItems(m.data); setEmbs(e.data); setServs(s.data);
      if (u) setUsers(u.data);
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error("Trabajos load failed:", ex);
      const status = ex?.response?.status || "?";
      const detail = ex?.response?.data?.detail || ex?.message || "Error desconocido";
      setErr(`[${status}] ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Esperar a que el AuthContext termine de validar el token antes de pedir datos.
    // Si disparamos la llamada antes, el token puede no estar listo y el backend devuelve 401.
    if (user === null) return;      // null = aún comprobando
    if (user === false) return;     // false = no logeado (Protected redirige)
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const filtered = useMemo(() => {
    if (filter === "en_curso") return items.filter((x) => x.status === "en_curso" || x.status === "pausado");
    if (filter === "programado") return items.filter((x) => x.status === "programado" || x.status === "retraso");
    return items.filter((x) => x.status === filter);
  }, [items, filter]);

  const embMap = useMemo(() => Object.fromEntries(embs.map((x) => [x.id, x])), [embs]);
  const userMap = useMemo(() => Object.fromEntries(users.map((x) => [x.id, x])), [users]);
  const servMap = useMemo(() => Object.fromEntries(servs.map((x) => [x.id, x])), [servs]);

  return (
    <div className="px-4 sm:px-5 pt-8 sns-fade-up" data-testid="page-trabajos">
      <div className="flex items-start justify-between mb-5 gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="sns-title text-2xl sm:text-3xl truncate">{t("trabajos.title")}</h1>
          <p className="text-slate-500 text-sm mt-1">{t("trabajos.count", { n: items.length })}</p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          {user?.role === "admin" && (
            <>
              <button
                onClick={() => nav("/gestion/servicios")}
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white border border-[var(--border)] flex items-center justify-center text-slate-600 hover:bg-slate-50"
                aria-label={t("trabajos.manage_services")}
                data-testid="btn-gestion-servicios"
              >
                <Sparkles size={18} />
              </button>
              <button onClick={() => nav("/calendario")} className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white border border-[var(--border)] flex items-center justify-center text-slate-600 hover:bg-slate-50" data-testid="btn-calendar">
                <Calendar size={18} />
              </button>
              <button
                onClick={() => nav("/nuevo-mantenimiento")}
                className="sns-fab w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center"
                data-testid="btn-new-mantenimiento"
                aria-label={t("trabajos.new")}
              >
                <Plus size={20} strokeWidth={2.5} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5 mb-5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-2 py-2.5 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-all flex items-center justify-center gap-1.5 ${
              filter === f.key
                ? "bg-[var(--ink)] text-white"
                : "bg-white text-slate-600 border border-[var(--border)]"
            }`}
            data-testid={`filter-${f.key}`}
          >
            <span>{f.label}</span>
            <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold tabular-nums ${
              filter === f.key ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"
            }`}>
              {counts[f.key]}
            </span>
          </button>
        ))}
      </div>

      {loading ? <Spinner label={t("common.loading")} /> : err ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-800 flex flex-col gap-3" data-testid="trabajos-error">
          <div className="font-semibold">⚠️ No se pudo cargar los trabajos</div>
          <div className="font-mono text-xs break-all opacity-80">{err}</div>
          <div className="font-mono text-[10px] opacity-60 bg-white rounded p-2 border border-red-100">
            <div>API: {process.env.REACT_APP_BACKEND_URL || "(no definido)"}</div>
            <div>Usuario: {user?.email || "?"} ({user?.role || "?"})</div>
            <div>Tenant: {user?.tenant_id?.slice(0, 20) || "?"}</div>
          </div>
          <button
            onClick={reload}
            className="self-start bg-red-600 text-white px-4 py-2 rounded-lg font-semibold text-sm active:scale-95"
            data-testid="btn-reload-trabajos"
          >
            Reintentar
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="space-y-4">
          <EmptyState title={t("trabajos.empty.title")} subtitle={t("trabajos.empty.sub")} />
          {items.length > 0 && (() => {
            // Sugerir el primer tab con trabajos (diferente al actual)
            const suggest = FILTERS.find(f => f.key !== filter && counts[f.key] > 0);
            if (!suggest) return null;
            return (
              <div className="text-center space-y-3">
                <p className="text-sm text-slate-500">
                  {items.length} {items.length === 1 ? "trabajo" : "trabajos"} en total
                </p>
                <button
                  onClick={() => setFilter(suggest.key)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--ink)] text-white text-sm font-semibold active:scale-95 transition-transform"
                  data-testid="btn-switch-filter"
                >
                  Ver {counts[suggest.key]} en "{suggest.label}" →
                </button>
              </div>
            );
          })()}
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((m) => (
            <JobCard key={m.id} m={m} emb={embMap[m.embarcacion_id]} userMap={userMap} servMap={servMap} lang={lang} />
          ))}
        </div>
      )}
    </div>
  );
}

const JobCard = ({ m, emb, userMap, servMap, lang }) => {
  const d = splitDate(m.start_at, lang);
  const empleados = (m.empleado_ids || []).map((id) => userMap[id]).filter(Boolean);
  const doneCount = (m.services || []).filter((s) => s.realizado).length;
  const totalCount = (m.services || []).length;
  return (
    <Link to={`/trabajos/${m.id}`} className="sns-card p-4 block hover:shadow-md transition-shadow" data-testid={`job-${m.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-xl bg-[var(--ink)] text-white flex flex-col items-center justify-center font-bold shrink-0">
          <div className="text-2xl leading-none">{d.day}</div>
          <div className="text-[10px] mt-1 opacity-80">{d.month}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="sns-title text-lg truncate">{emb?.name || "—"}</h3>
            <StatusPill status={m.status} />
          </div>
          <p className="text-sm text-slate-500 truncate">{emb?.matricula || emb?.description || "—"}</p>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-1.5 text-sm text-slate-600">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-slate-400" />
          <span>{m.end_at ? `${fmtTime(m.start_at)} - ${fmtTime(m.end_at)}` : fmtTime(m.start_at)}</span>
        </div>
        {emb?.puerto && (
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-slate-400" />
            <span>{emb.puerto}</span>
          </div>
        )}
        {empleados.length > 0 && (
          <div className="flex items-center gap-2">
            <UsersIcon size={14} className="text-slate-400" />
            <span className="truncate">{empleados.map((u) => u.name).join(", ")}</span>
          </div>
        )}
      </div>

      {totalCount > 0 && (
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--border)]">
          <div className="flex flex-wrap gap-1.5">
            {(m.services || []).slice(0, 6).map((s) => {
              const sv = servMap[s.service_id];
              return (
                <span key={s.service_id} className={`sns-pill ${s.realizado ? "bg-green-100 text-green-700" : "bg-[var(--sky)] text-[var(--sky-text)]"}`}>
                  {s.realizado && "✓ "}{sv?.code || sv?.name?.slice(0, 8) || "S"}
                </span>
              );
            })}
          </div>
          <span className="text-xs font-bold text-slate-500 tabular-nums">{doneCount}/{totalCount}</span>
        </div>
      )}
    </Link>
  );
};
